/* eslint-disable react/no-unknown-property, react/no-unescaped-entities */
/**
 * Structural inspection panel — as-built vs as-designed 3D checks.
 *
 * Workflow (top → bottom of the panel):
 *   1. Pick or create a Session (each session = one fabricated item under
 *      inspection).
 *   2. Upload the item's DXF (Save-As DXF from AutoCAD works).
 *   3. Align the model to the world frame — either:
 *        • Kabsch: shoot ≥3 reference marks, click each one on the model
 *        • Manual: enter model X/Y/Z + heading
 *   4. Check points: point the laser at a real-world feature, click the
 *      corresponding target on the 3D model, hit "Add check". The system
 *      returns the XYZ deviation and total distance in mm.
 *   5. Export a deviations CSV for the site file.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Grid, Html } from "@react-three/drei";
import * as THREE from "three";
import {
  Upload,
  Plus,
  Trash,
  Target,
  Crosshair,
  DownloadSimple,
  Ruler,
  Cube,
  Warning,
  Check as CheckIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { structural, station, fmt } from "../lib/api";

// -------- Session picker + top-level state -----------------------------

export const StructuralPanel = ({ state, disabled }) => {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [session, setSession] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Load the session list on mount + whenever we bump the refresh tick.
  useEffect(() => {
    structural.listSessions().then(setSessions).catch(() => {});
  }, [refreshTick]);

  // Auto-select the first session so the surveyor lands somewhere useful.
  useEffect(() => {
    if (!sid && sessions.length) setSid(sessions[0].id);
  }, [sessions, sid]);

  // Full session document (mesh + anchors + checks) with re-fetch helper.
  const refresh = useCallback(async () => {
    if (!sid) {
      setSession(null);
      return;
    }
    try {
      setSession(await structural.get(sid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Session load failed");
    }
  }, [sid]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const bumpList = () => setRefreshTick((n) => n + 1);

  const createSession = async () => {
    const name = window.prompt("Name this inspection (e.g. Beam A1):");
    if (!name) return;
    try {
      const s = await structural.create(name);
      setSid(s.id);
      bumpList();
      toast.success(`Session "${s.name}" created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const deleteSession = async () => {
    if (!sid) return;
    if (!window.confirm(`Delete session "${session?.name ?? sid}"?`)) return;
    try {
      await structural.remove(sid);
      setSid(null);
      setSession(null);
      bumpList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="structural-panel">
      {/* Session picker bar */}
      <div className="panel">
        <div className="panel-header">
          <span>STRUCTURAL INSPECTION</span>
          <div className="flex items-center gap-2">
            <select
              data-testid="structural-session-select"
              value={sid || ""}
              onChange={(e) => setSid(e.target.value || null)}
              className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1 text-xs"
            >
              <option value="">— pick a session —</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.aligned ? "  ✓" : ""}
                  {s.n_checks ? `  · ${s.n_checks} checks` : ""}
                </option>
              ))}
            </select>
            <button
              data-testid="structural-new-btn"
              onClick={createSession}
              className="btn-tac !min-h-[32px] px-3 text-[11px]"
            >
              <span className="flex items-center gap-1">
                <Plus size={12} weight="bold" /> New
              </span>
            </button>
            <button
              data-testid="structural-delete-btn"
              onClick={deleteSession}
              disabled={!sid}
              className="btn-tac !min-h-[32px] px-3 text-[11px] disabled:opacity-40"
              data-variant="danger"
            >
              <Trash size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {!session ? (
        <div className="panel">
          <div className="p-8 text-center text-sm text-[var(--text-dim)]">
            <Cube size={32} weight="light" className="mx-auto mb-3 opacity-40" />
            Pick or create a session above to start an inspection.
            <div className="mt-2 text-[11px] text-[var(--text-mute)]">
              Each session holds one fabricated item's DXF, reference anchors,
              and check points.
            </div>
          </div>
        </div>
      ) : (
        <StructuralSession
          session={session}
          state={state}
          disabled={disabled}
          onChanged={(s) => {
            setSession(s);
            bumpList();
          }}
        />
      )}
    </div>
  );
};

// -------- One-session working area (viewer + wizards) ------------------

const StructuralSession = ({ session, state, disabled, onChanged }) => {
  // The point currently picked in the 3D viewer — the "target" the
  // surveyor is about to associate with the next survey shot.
  const [pickedModelPt, setPickedModelPt] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");

  const currentWorldXYZ = state
    ? [state.x, state.y, state.z]
    : null;
  const canShoot = !!state && Number.isFinite(state.x) && Number.isFinite(state.y)
                   && Number.isFinite(state.z);

  const measureAndCommit = async (mode) => {
    // "mode" is one of "anchor" or "check". We take a fresh EDM shot on
    // the total station (COORD reflectorless) then commit against the
    // currently-picked model target.
    if (!pickedModelPt) {
      toast.error("Click a target on the 3D model first");
      return;
    }
    if (!labelDraft.trim()) {
      toast.error("Type a label (e.g. 'Corner NE') first");
      return;
    }
    try {
      let shot = state;
      // Fire a fresh COORD measurement so we don't reuse a stale one.
      try {
        shot = await station.measure("coord");
      } catch (e) {
        // If the bridge is offline (SIM mode), fall back to whatever the
        // live state has — useful for demoing without hardware.
        if (!currentWorldXYZ) throw e;
      }
      const worldXYZ = shot && Number.isFinite(shot.x)
        ? [shot.x, shot.y, shot.z]
        : currentWorldXYZ;
      if (!worldXYZ) {
        toast.error("No world coordinate available — measure first");
        return;
      }
      const payload = {
        label: labelDraft.trim(),
        model_xyz: pickedModelPt,
        world_xyz: worldXYZ,
      };
      const updated = mode === "anchor"
        ? await structural.addAnchor(session.id, payload)
        : await structural.addCheck(session.id, payload);
      onChanged(updated);
      setLabelDraft("");
      setPickedModelPt(null);
      toast.success(`${mode === "anchor" ? "Anchor" : "Check"} "${payload.label}" saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Measurement failed");
    }
  };

  return (
    <div className="grid gap-3 lg:grid-cols-12">
      {/* 3D viewer — left ⅔ */}
      <div className="panel lg:col-span-8">
        <div className="panel-header">
          <span>
            {session.name}
            {session.dxf_filename && (
              <span className="ml-2 text-[10px] text-[var(--text-dim)]">
                · {session.dxf_filename}
              </span>
            )}
          </span>
          <UploadDxfButton session={session} onChanged={onChanged} />
        </div>
        <div className="relative aspect-[16/10] bg-[var(--surface-2)]">
          {session.mesh ? (
            <ModelViewer
              mesh={session.mesh}
              anchors={session.anchors}
              checks={session.checks}
              tolerance_m={session.tolerance_mm / 1000}
              pickedModelPt={pickedModelPt}
              onPick={setPickedModelPt}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-center">
              <div>
                <Cube size={40} weight="light" className="mx-auto mb-2 opacity-40" />
                <div className="text-sm text-[var(--text-dim)]">
                  Upload a DXF to render the item.
                </div>
                <div className="mt-1 text-[10px] text-[var(--text-mute)]">
                  Save-As DXF (2018 format) from AutoCAD, Revit, Tekla, Inventor…
                </div>
              </div>
            </div>
          )}
          {session.mesh?.warning && (
            <div className="absolute left-2 right-2 top-2 rounded-sm border border-[var(--amber)] bg-black/70 px-2 py-1 text-[10px] text-[var(--amber)]">
              <Warning size={10} weight="bold" className="mr-1 inline" />
              {session.mesh.warning}
            </div>
          )}
        </div>
        {/* Picked-target readout + label + shoot buttons */}
        <div className="grid grid-cols-12 items-center gap-2 border-t border-[var(--border)] p-3">
          <div className="col-span-4 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            Model target
            <div className="mt-0.5 font-mono text-[11px] text-[var(--text)]">
              {pickedModelPt
                ? `${fmt(pickedModelPt[0], 3)}, ${fmt(pickedModelPt[1], 3)}, ${fmt(pickedModelPt[2], 3)} m`
                : <span className="text-[var(--text-mute)]">click on the model</span>}
            </div>
          </div>
          <input
            data-testid="structural-label-input"
            type="text"
            placeholder="Label (e.g. Corner NE, Bolt-A3)"
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            maxLength={40}
            className="col-span-3 rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1.5 text-xs"
          />
          <button
            data-testid="structural-add-anchor-btn"
            onClick={() => measureAndCommit("anchor")}
            disabled={!pickedModelPt || !labelDraft.trim() || !canShoot || disabled}
            className="btn-tac col-span-2 !min-h-[36px] text-[11px] disabled:opacity-40"
            data-variant="measure"
            title="Add an alignment anchor — the total station shoots the current aim, and pairs it with the picked model target."
          >
            <span className="flex items-center justify-center gap-1">
              <Crosshair size={12} weight="bold" /> Anchor
            </span>
          </button>
          <button
            data-testid="structural-add-check-btn"
            onClick={() => measureAndCommit("check")}
            disabled={!pickedModelPt || !labelDraft.trim() || !canShoot || disabled || !session.R}
            className="btn-tac col-span-3 !min-h-[36px] text-[11px] disabled:opacity-40"
            data-variant="primary"
            title="Add a deviation check — model is already aligned, so the reported deviation is live."
          >
            <span className="flex items-center justify-center gap-1">
              <Target size={12} weight="bold" /> Add check
            </span>
          </button>
        </div>
      </div>

      {/* Sidebar — right ⅓ */}
      <div className="flex flex-col gap-3 lg:col-span-4">
        <AlignmentPanel session={session} onChanged={onChanged} />
        <TolerancePanel session={session} onChanged={onChanged} />
        <AnchorsTable session={session} onChanged={onChanged} />
      </div>

      {/* Checks table — full width below */}
      <div className="lg:col-span-12">
        <ChecksTable session={session} onChanged={onChanged} />
      </div>
    </div>
  );
};

// -------- 3D viewer -----------------------------------------------------

const ModelViewer = ({ mesh, anchors, checks, tolerance_m, pickedModelPt, onPick }) => {
  // Compute a decent initial camera so the whole item fits in view.
  const bbox = useMemo(() => {
    const mn = mesh.bbox_min, mx = mesh.bbox_max;
    const cx = (mn[0] + mx[0]) / 2;
    const cy = (mn[1] + mx[1]) / 2;
    const cz = (mn[2] + mx[2]) / 2;
    const size = Math.max(
      mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2], 1e-3,
    );
    return { center: [cx, cy, cz], size };
  }, [mesh]);

  // Flatten segment buffer for THREE.LineSegments.
  const segGeometry = useMemo(() => {
    const flat = new Float32Array(mesh.segments.length * 6);
    for (let i = 0; i < mesh.segments.length; i++) {
      const s = mesh.segments[i];
      flat.set(s, i * 6);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(flat, 3));
    return g;
  }, [mesh]);

  // Triangles for click-picking + shading.
  const triGeometry = useMemo(() => {
    if (!mesh.triangles.length) return null;
    const flat = new Float32Array(mesh.triangles.length * 9);
    for (let i = 0; i < mesh.triangles.length; i++) {
      const t = mesh.triangles[i];
      flat.set(t, i * 9);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(flat, 3));
    g.computeVertexNormals();
    return g;
  }, [mesh]);

  // Raycasting on LINES needs a threshold — set it proportional to the
  // model size so it works on both a 500 mm gusset and a 50 m truss.
  const linePickThreshold = bbox.size * 0.004;

  return (
    <Canvas
      camera={{
        position: [
          bbox.center[0] + bbox.size * 1.5,
          bbox.center[1] - bbox.size * 1.5,
          bbox.center[2] + bbox.size * 1.1,
        ],
        up: [0, 0, 1],
        fov: 45,
        near: bbox.size * 0.001,
        far: bbox.size * 100,
      }}
      onCreated={({ raycaster }) => {
        raycaster.params.Line.threshold = linePickThreshold;
        raycaster.params.Points.threshold = linePickThreshold;
      }}
      style={{ background: "var(--surface-2)" }}
    >
      <ambientLight intensity={0.7} />
      <directionalLight position={[10, 10, 20]} intensity={0.4} />
      <OrbitControls
        target={bbox.center}
        makeDefault
        enablePan
        enableRotate
        enableZoom
      />
      {/* Ground grid at model bbox bottom for spatial reference */}
      <Grid
        position={[bbox.center[0], bbox.center[1], mesh.bbox_min[2]]}
        args={[bbox.size * 3, bbox.size * 3]}
        cellSize={bbox.size * 0.05}
        cellThickness={0.5}
        cellColor="#3a4657"
        sectionSize={bbox.size * 0.25}
        sectionThickness={1}
        sectionColor="#5b6a7e"
        fadeDistance={bbox.size * 4}
        fadeStrength={1.2}
        infiniteGrid={false}
        rotation={[Math.PI / 2, 0, 0]}
      />
      {/* Lines from DXF */}
      <lineSegments
        geometry={segGeometry}
        onClick={(ev) => {
          ev.stopPropagation();
          onPick(ev.point.toArray());
        }}
      >
        <lineBasicMaterial color="#7fc9ff" linewidth={1} />
      </lineSegments>
      {/* Triangles from DXF — semi-transparent so lines beneath show through. */}
      {triGeometry && (
        <mesh
          geometry={triGeometry}
          onClick={(ev) => {
            ev.stopPropagation();
            onPick(ev.point.toArray());
          }}
        >
          <meshStandardMaterial
            color="#2b3f55"
            transparent
            opacity={0.35}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      {/* Picked-target marker */}
      {pickedModelPt && (
        <>
          <mesh position={pickedModelPt}>
            <sphereGeometry args={[bbox.size * 0.008, 16, 12]} />
            <meshBasicMaterial color="#ffb020" />
          </mesh>
          <Html position={pickedModelPt} distanceFactor={bbox.size * 4}>
            <div className="pointer-events-none rounded-sm border border-[#ffb020] bg-black/70 px-1 py-0.5 text-[9px] uppercase tracking-widest text-[#ffb020]">
              target
            </div>
          </Html>
        </>
      )}
      {/* Anchors — orange crosshairs on the model */}
      {anchors.map((a) => (
        <mesh key={a.id} position={a.model_xyz}>
          <sphereGeometry args={[bbox.size * 0.006, 12, 10]} />
          <meshBasicMaterial color="#ff7043" />
        </mesh>
      ))}
      {/* Checks — colour-coded by deviation, small deltas green, big red */}
      {checks.map((c) => {
        const dev = c.deviation_m ?? 0;
        const color = c.deviation_m == null
          ? "#8899a8"
          : dev <= tolerance_m
            ? "#3fd07a"
            : dev <= tolerance_m * 2
              ? "#f9a03f"
              : "#ff4d4d";
        return (
          <mesh key={c.id} position={c.model_xyz}>
            <sphereGeometry args={[bbox.size * 0.007, 12, 10]} />
            <meshBasicMaterial color={color} />
          </mesh>
        );
      })}
    </Canvas>
  );
};

// -------- Upload button (DXF) ------------------------------------------

const UploadDxfButton = ({ session, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".dwg")) {
      toast.error("DWG isn't supported — Save-As DXF in AutoCAD first.");
      return;
    }
    setBusy(true);
    try {
      const updated = await structural.upload(session.id, file);
      onChanged(updated);
      toast.success(`Loaded ${file.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".dxf"
        onChange={pick}
        className="hidden"
        data-testid="structural-upload-input"
      />
      <button
        data-testid="structural-upload-btn"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="btn-tac !min-h-[28px] px-2 text-[10px] disabled:opacity-40"
      >
        <span className="flex items-center gap-1">
          <Upload size={11} weight="bold" /> {busy ? "Loading…" : session.mesh ? "Replace DXF" : "Upload DXF"}
        </span>
      </button>
    </>
  );
};

// -------- Alignment wizard ---------------------------------------------

const AlignmentPanel = ({ session, onChanged }) => {
  const [manualOpen, setManualOpen] = useState(false);
  const rms_mm = session.align_rms_m == null
    ? null
    : session.align_rms_m * 1000;
  const alignBadge = !session.R
    ? { text: "not aligned", color: "text-[var(--amber)]" }
    : session.align_method === "kabsch"
      ? { text: `Kabsch · ${rms_mm != null ? rms_mm.toFixed(1) + " mm RMS" : ""}`,
          color: rms_mm != null && rms_mm <= 3 ? "text-[var(--neon)]" : "text-[var(--amber)]" }
      : { text: "manual placement", color: "text-[var(--neon)]" };

  return (
    <div className="panel">
      <div className="panel-header">
        <span>ALIGNMENT</span>
        <span className={`text-[10px] uppercase tracking-widest ${alignBadge.color}`}>
          {alignBadge.text}
        </span>
      </div>
      <div className="space-y-3 p-3 text-[11px]">
        <p className="text-[var(--text-dim)]">
          <b>Kabsch</b> — best-fit rigid transform from ≥3 anchor pairs.
          Add anchors by clicking model targets + shooting real-world
          reference points. Recomputes automatically as anchors are added.
        </p>
        <button
          data-testid="structural-manual-toggle"
          onClick={() => setManualOpen((v) => !v)}
          className="w-full rounded-sm border border-[var(--border)] px-2 py-1.5 text-left text-[10px] uppercase tracking-widest text-[var(--text-dim)] hover:text-[var(--text)]"
        >
          {manualOpen ? "▼" : "▶"} Manual XYZ + heading (skip Kabsch)
        </button>
        {manualOpen && <ManualAlignForm session={session} onChanged={onChanged} />}
      </div>
    </div>
  );
};

const ManualAlignForm = ({ session, onChanged }) => {
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [z, setZ] = useState(0);
  const [hdg, setHdg] = useState(0);
  const [tilt, setTilt] = useState(0);
  const [roll, setRoll] = useState(0);
  const apply = async () => {
    try {
      const updated = await structural.alignManual(session.id, {
        x: Number(x) || 0,
        y: Number(y) || 0,
        z: Number(z) || 0,
        heading_deg: Number(hdg) || 0,
        tilt_deg: Number(tilt) || 0,
        roll_deg: Number(roll) || 0,
      });
      onChanged(updated);
      toast.success("Manual alignment applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alignment failed");
    }
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <NumInput label="X (m)" value={x} onChange={setX} testid="manual-x" />
        <NumInput label="Y (m)" value={y} onChange={setY} testid="manual-y" />
        <NumInput label="Z (m)" value={z} onChange={setZ} testid="manual-z" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <NumInput label="Heading°" value={hdg} onChange={setHdg} testid="manual-hdg" />
        <NumInput label="Tilt°" value={tilt} onChange={setTilt} testid="manual-tilt" />
        <NumInput label="Roll°" value={roll} onChange={setRoll} testid="manual-roll" />
      </div>
      <button
        data-testid="structural-apply-manual-btn"
        onClick={apply}
        className="btn-tac w-full !min-h-[32px] text-[11px]"
      >
        Apply manual placement
      </button>
    </div>
  );
};

const NumInput = ({ label, value, onChange, testid }) => (
  <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
    {label}
    <input
      data-testid={testid}
      type="number"
      step="any"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-1.5 py-1 font-mono text-[11px]"
    />
  </label>
);

// -------- Tolerance ----------------------------------------------------

const TolerancePanel = ({ session, onChanged }) => {
  const [tol, setTol] = useState(session.tolerance_mm);
  useEffect(() => setTol(session.tolerance_mm), [session.tolerance_mm]);
  const apply = async () => {
    try {
      const updated = await structural.setTolerance(session.id, Number(tol) || 1);
      onChanged(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tolerance failed");
    }
  };
  return (
    <div className="panel">
      <div className="panel-header">
        <span>PASS TOLERANCE</span>
        <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">mm</span>
      </div>
      <div className="flex items-center gap-2 p-3">
        <input
          data-testid="structural-tolerance-input"
          type="number"
          step="0.1"
          min="0.1"
          value={tol}
          onChange={(e) => setTol(e.target.value)}
          onBlur={apply}
          className="w-24 rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1 font-mono text-sm"
        />
        <div className="text-[10px] leading-tight text-[var(--text-dim)]">
          Checks within this radius = green,<br />up to 2× = amber, beyond = red.
        </div>
      </div>
    </div>
  );
};

// -------- Anchors table ------------------------------------------------

const AnchorsTable = ({ session, onChanged }) => {
  const remove = async (aid) => {
    try {
      const updated = await structural.removeAnchor(session.id, aid);
      onChanged(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <div className="panel">
      <div className="panel-header">
        <span>ANCHORS ({session.anchors.length})</span>
        <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
          ≥3 needed for Kabsch
        </span>
      </div>
      {session.anchors.length === 0 ? (
        <div className="p-3 text-[11px] text-[var(--text-mute)]">
          None yet. Click a target on the model, type a label, aim & shoot,
          then hit <b>Anchor</b>.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {session.anchors.map((a) => (
            <li key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-[11px]">
              <Crosshair size={11} weight="bold" className="text-[#ff7043]" />
              <span className="flex-1 truncate font-mono text-[var(--text)]">{a.label}</span>
              <span className="font-mono text-[10px] text-[var(--text-dim)]">
                {fmt(a.model_xyz[0], 2)}, {fmt(a.model_xyz[1], 2)}, {fmt(a.model_xyz[2], 2)}
              </span>
              <button
                onClick={() => remove(a.id)}
                className="rounded-sm px-1 py-0.5 text-[var(--text-dim)] hover:text-[var(--danger)]"
                title="Remove anchor"
              >
                <Trash size={11} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// -------- Checks table -------------------------------------------------

const ChecksTable = ({ session, onChanged }) => {
  const tol_mm = session.tolerance_mm;
  const remove = async (cid) => {
    try {
      const updated = await structural.removeCheck(session.id, cid);
      onChanged(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const summary = useMemo(() => {
    const total = session.checks.length;
    const withDev = session.checks.filter((c) => c.deviation_m != null);
    const pass = withDev.filter((c) => c.within_tol).length;
    const fail = withDev.length - pass;
    const maxDev = withDev.reduce(
      (m, c) => Math.max(m, (c.deviation_m ?? 0) * 1000), 0,
    );
    return { total, pass, fail, maxDev };
  }, [session]);
  return (
    <div className="panel">
      <div className="panel-header">
        <span>CHECKS ({session.checks.length})</span>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
          <span className="text-[var(--neon)]">
            <CheckIcon size={10} weight="bold" className="inline" /> {summary.pass} pass
          </span>
          <span className="text-[var(--danger)]">
            <Warning size={10} weight="bold" className="inline" /> {summary.fail} fail
          </span>
          <span className="text-[var(--text-dim)]">
            max {summary.maxDev.toFixed(1)} mm
          </span>
          <a
            data-testid="structural-csv-btn"
            href={structural.csvUrl(session.id)}
            download={`${session.name}_deviations.csv`}
            className="btn-tac !min-h-[26px] px-2 text-[10px]"
          >
            <span className="flex items-center gap-1">
              <DownloadSimple size={10} weight="bold" /> CSV
            </span>
          </a>
        </div>
      </div>
      {session.checks.length === 0 ? (
        <div className="p-4 text-center text-[11px] text-[var(--text-mute)]">
          No checks yet. {session.R
            ? "Click a model target, type a label, aim & shoot, then hit Add check."
            : "Align the model first (≥3 anchors) before adding checks."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="border-b border-[var(--border)] text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
              <tr className="text-left">
                <th className="px-3 py-1.5">Label</th>
                <th className="px-2 py-1.5">Model X/Y/Z (m)</th>
                <th className="px-2 py-1.5">Shot X/Y/Z (m)</th>
                <th className="px-2 py-1.5">Δx</th>
                <th className="px-2 py-1.5">Δy</th>
                <th className="px-2 py-1.5">Δz</th>
                <th className="px-2 py-1.5 text-right">|Δ| (mm)</th>
                <th className="px-2 py-1.5">Verdict</th>
                <th className="w-10 px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {session.checks.map((c) => {
                const dev_mm = c.deviation_m != null ? c.deviation_m * 1000 : null;
                const verdictColor =
                  dev_mm == null ? "text-[var(--text-mute)]"
                  : dev_mm <= tol_mm ? "text-[var(--neon)]"
                  : dev_mm <= tol_mm * 2 ? "text-[var(--amber)]"
                  : "text-[var(--danger)]";
                const dx = c.deviation_xyz ? c.deviation_xyz[0] * 1000 : null;
                const dy = c.deviation_xyz ? c.deviation_xyz[1] * 1000 : null;
                const dz = c.deviation_xyz ? c.deviation_xyz[2] * 1000 : null;
                return (
                  <tr key={c.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)]">
                    <td className="px-3 py-1.5 font-mono">{c.label}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-[var(--text-dim)]">
                      {fmt(c.model_xyz[0], 3)}, {fmt(c.model_xyz[1], 3)}, {fmt(c.model_xyz[2], 3)}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-[var(--text-dim)]">
                      {fmt(c.world_xyz[0], 3)}, {fmt(c.world_xyz[1], 3)}, {fmt(c.world_xyz[2], 3)}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{dx != null ? dx.toFixed(1) : "—"}</td>
                    <td className="px-2 py-1.5 font-mono">{dy != null ? dy.toFixed(1) : "—"}</td>
                    <td className="px-2 py-1.5 font-mono">{dz != null ? dz.toFixed(1) : "—"}</td>
                    <td className={`px-2 py-1.5 text-right font-mono font-bold ${verdictColor}`}>
                      {dev_mm != null ? dev_mm.toFixed(1) : "—"}
                    </td>
                    <td className={`px-2 py-1.5 text-[10px] uppercase tracking-widest ${verdictColor}`}>
                      {dev_mm == null ? "no align"
                       : c.within_tol ? "PASS"
                       : dev_mm <= tol_mm * 2 ? "WARN"
                       : "FAIL"}
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        onClick={() => remove(c.id)}
                        className="text-[var(--text-dim)] hover:text-[var(--danger)]"
                        title="Remove check"
                      >
                        <Trash size={11} weight="bold" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
